import {useEffect, useState} from "react";
import {DialogBody, DialogFooter, MenuItem} from "@blueprintjs/core";
import {ItemRenderer, Select} from "@blueprintjs/select";
import {DEFAULT_POPOVER_MODIFIERS} from "../../../constants/constants";
import {CancelButton, StyledButton} from "../../common/StyledButtons";
import {toVehicleId} from "../utils/RoutePlanning.utils";
import DriverInfiniteSelect from "./DriverInfiniteSelect";
import {
  DialogSubtitle,
  DriverPickerButton,
  DriverPickerHint,
  DriverPickerName,
  DriverPickerPlaceholder,
  DriverPickerValue,
  FieldLabel,
  ModeToggle,
  ResultDivider,
  ResultName,
  ResultRow,
  ResultTarget,
  SelectRow,
  StyledDialog
} from "../styles/styleds";
import {ChangeDriverOption, SwapRouteOption} from "../types/types";

export type SwapChangeMode = "swap" | "change";
export type ChangeDriverScope = "selected" | "other";

/** Normalized option for the driver picker (value = routeId in Swap, driverId in Change). */
type DriverPickerItem = {value: string; driverLabel: string; routeLabel: string | null};

/**
 * Route hint ("currently on *Route B*", route letter italic) — shown ONLY on the selected
 * value, and only when that driver currently has a route. Options list drivers by name only.
 */
const routeHint = (routeLabel: string | null) =>
  routeLabel ? (
    <DriverPickerHint>
      currently on <em>{routeLabel}</em>
    </DriverPickerHint>
  ) : null;

const renderDriverItem: ItemRenderer<DriverPickerItem> = (item, {handleClick, handleFocus, modifiers}) => (
  <MenuItem
    key={item.value}
    active={modifiers.active}
    onClick={handleClick}
    onFocus={handleFocus}
    text={item.driverLabel}
  />
);

/** Driver dropdown: name on the left, muted route hint right-aligned (Figma 4063-9863). */
const DriverPicker = ({
  items,
  value,
  onSelect,
  testId,
  showRouteHint
}: {
  items: DriverPickerItem[];
  value: string;
  onSelect: (value: string) => void;
  testId: string;
  /** Show "currently on Route X" on the selected value — Change mode only, not Swap. */
  showRouteHint: boolean;
}) => {
  const selected = items.find((item) => item.value === value) ?? null;
  return (
    <Select<DriverPickerItem>
      items={items}
      filterable={false}
      itemRenderer={renderDriverItem}
      onItemSelect={(item) => onSelect(item.value)}
      activeItem={selected}
      // The scope selector always shows both scopes now — an empty scope opens an empty picker.
      noResults={<MenuItem disabled text="No drivers found" />}
      popoverProps={{minimal: true, matchTargetWidth: true, modifiers: DEFAULT_POPOVER_MODIFIERS}}
    >
      <DriverPickerButton
        data-testid={testId}
        alignText="left"
        endIcon="caret-down"
        text={
          selected ? (
            <DriverPickerValue>
              <DriverPickerName>{selected.driverLabel}</DriverPickerName>
              {showRouteHint ? routeHint(selected.routeLabel) : null}
            </DriverPickerValue>
          ) : (
            <DriverPickerPlaceholder>Select driver...</DriverPickerPlaceholder>
          )
        }
      />
    </Select>
  );
};

type ScopeItem = {value: ChangeDriverScope; label: string};

const SCOPE_ITEMS: ScopeItem[] = [
  {value: "selected", label: "Currently selected"},
  {value: "other", label: "Other drivers"}
];

const renderScopeItem: ItemRenderer<ScopeItem> = (item, {handleClick, handleFocus, modifiers}) => (
  <MenuItem key={item.value} active={modifiers.active} onClick={handleClick} onFocus={handleFocus} text={item.label} />
);

/** Change-mode scope picker — same Blueprint Select styling as the driver picker (not a native select). */
const ScopePicker = ({value, onSelect}: {value: ChangeDriverScope; onSelect: (value: ChangeDriverScope) => void}) => {
  const selected = SCOPE_ITEMS.find((item) => item.value === value) ?? SCOPE_ITEMS[0];
  return (
    <Select<ScopeItem>
      items={SCOPE_ITEMS}
      filterable={false}
      itemRenderer={renderScopeItem}
      onItemSelect={(item) => onSelect(item.value)}
      activeItem={selected}
      popoverProps={{minimal: true, matchTargetWidth: true, modifiers: DEFAULT_POPOVER_MODIFIERS}}
    >
      <DriverPickerButton
        data-testid="swap-change-scope-select"
        alignText="left"
        endIcon="caret-down"
        text={selected.label}
      />
    </Select>
  );
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  currentDriverName: string;
  /** Letter label of the route being acted on, e.g. "Route A". */
  currentRouteLabel: string;
  /** Swap targets — route-owning drivers only, one option per route row (BR5). */
  swapOptions: SwapRouteOption[];
  /** Change · "Currently selected" — drivers already in this planning run. */
  currentlySelectedOptions: ChangeDriverOption[];
  /** Change · "Other drivers" — active drivers outside the run (accumulated, search-filtered page). */
  otherDriverOptions: ChangeDriverOption[];
  /** DP-3039: infinite-scroll + server-side search state for the Other-drivers dropdown. */
  otherDriversLoading?: boolean;
  otherDriversHasMore?: boolean;
  onOtherDriversSearch?: (query: string) => void;
  onOtherDriversLoadMore?: () => void;
  onChange: (targetVehicleId: string) => void;
  onSwap: (targetRouteId: string) => void;
};

const ChangeDriverDialog = ({
  isOpen,
  onClose,
  currentDriverName,
  currentRouteLabel,
  swapOptions,
  currentlySelectedOptions,
  otherDriverOptions,
  otherDriversLoading = false,
  otherDriversHasMore = false,
  onOtherDriversSearch,
  onOtherDriversLoadMore,
  onChange,
  onSwap
}: Props) => {
  // null = not touched yet → fall back to the first enabled default for the current pools
  const [mode, setMode] = useState<SwapChangeMode | null>(null);
  const [scope, setScope] = useState<ChangeDriverScope | null>(null);
  const [selection, setSelection] = useState<string>("");
  // Label captured at selection time — the Other-drivers list is server-searched, so a picked
  // driver can drop out of the visible page; we keep its label for the button + Result preview.
  const [selectionLabel, setSelectionLabel] = useState<string>("");

  // Other-drivers list is server-searched, so its length reflects the CURRENT search — it must
  // not gate whether Change is offered (a no-match search would otherwise disable the tab).
  // Latch: once the pool has shown any driver, Other stays a valid scope for this dialog.
  const [otherScopeSeen, setOtherScopeSeen] = useState(false);
  useEffect(() => {
    if (otherDriverOptions.length > 0) setOtherScopeSeen(true);
  }, [otherDriverOptions.length]);
  const otherScopeAvailable = otherScopeSeen || otherDriverOptions.length > 0;

  const swapAvailable = swapOptions.length > 0;
  const changeAvailable = currentlySelectedOptions.length > 0 || otherScopeAvailable;
  const effectiveMode: SwapChangeMode = mode ?? (swapAvailable ? "swap" : "change");

  // The scope selector ALWAYS shows both scopes in Change mode; an empty scope simply opens an
  // empty driver picker ("No drivers found"). Default to whichever scope currently has drivers.
  const defaultScope: ChangeDriverScope = currentlySelectedOptions.length > 0 ? "selected" : "other";
  const effectiveScope: ChangeDriverScope = scope ?? defaultScope;

  const changeDriverOptions = effectiveScope === "selected" ? currentlySelectedOptions : otherDriverOptions;

  // Unify Swap (routeId-valued) and Change (driverId-valued) options for the driver picker.
  const driverItems: DriverPickerItem[] =
    effectiveMode === "swap"
      ? swapOptions.map((option) => ({
          value: option.routeId,
          driverLabel: option.driverLabel,
          routeLabel: option.routeLabel
        }))
      : changeDriverOptions.map((option) => ({
          value: toVehicleId(option),
          driverLabel: option.driverLabel,
          routeLabel: option.routeLabel
        }));

  const selectedSwapTarget =
    effectiveMode === "swap" ? swapOptions.find((option) => option.routeId === selection) : undefined;
  const targetDriverLabel = selectionLabel;

  // Capture the label at click time (see selectionLabel) so it survives list changes.
  const selectDriver = (value: string, label: string) => {
    setSelection(value);
    setSelectionLabel(label);
  };

  const clearSelection = () => {
    setSelection("");
    setSelectionLabel("");
  };

  const resetState = () => {
    setMode(null);
    setScope(null);
    clearSelection();
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  const handleSave = () => {
    if (!selection) return;
    if (effectiveMode === "swap") {
      onSwap(selection);
    } else {
      // "Currently selected" scope stores vehicleId ("driver:X" / "manifest:X");
      // "Other drivers" scope stores raw driverId string — normalize to vehicleId.
      const vehicleId = selection.includes(":") ? selection : `driver:${selection}`;
      onChange(vehicleId);
    }
    onClose();
    resetState();
  };

  return (
    <StyledDialog isOpen={isOpen} onClose={handleClose} title="Swap or Change Driver" style={{width: 520}}>
      <DialogBody>
        <DialogSubtitle data-testid="swap-change-subtitle">
          <em>{currentRouteLabel}</em> currently assigned to {currentDriverName}
        </DialogSubtitle>
        <ModeToggle
          data-testid="swap-change-mode-toggle"
          fill
          value={effectiveMode}
          onValueChange={(value) => {
            const next = value as SwapChangeMode;
            if (next === effectiveMode) return; // re-clicking the active tab must keep the selection
            setMode(next);
            clearSelection();
          }}
          options={[
            {label: "Swap driver", value: "swap", icon: "swap-horizontal", disabled: !swapAvailable},
            {label: "Change driver", value: "change", icon: "arrow-right", disabled: !changeAvailable}
          ]}
        />
        <FieldLabel>Select a driver</FieldLabel>
        <SelectRow hasScope={effectiveMode === "change"}>
          {effectiveMode === "change" && (
            <ScopePicker
              value={effectiveScope}
              onSelect={(next) => {
                if (next === effectiveScope) return; // re-picking the same scope keeps the selection
                setScope(next);
                clearSelection();
              }}
            />
          )}
          {effectiveMode === "change" && effectiveScope === "other" ? (
            <DriverInfiniteSelect
              testId="swap-change-driver-select"
              value={selection}
              selectedLabel={selectionLabel}
              options={otherDriverOptions.map((option) => ({
                driverId: option.driverId,
                driverLabel: option.driverLabel
              }))}
              loading={otherDriversLoading}
              hasMore={otherDriversHasMore}
              onSelect={(value) =>
                selectDriver(
                  value,
                  otherDriverOptions.find((option) => String(option.driverId) === value)?.driverLabel ?? ""
                )
              }
              onSearch={onOtherDriversSearch ?? (() => undefined)}
              onLoadMore={onOtherDriversLoadMore ?? (() => undefined)}
            />
          ) : (
            <DriverPicker
              testId="swap-change-driver-select"
              items={driverItems}
              value={selection}
              onSelect={(value) =>
                selectDriver(value, driverItems.find((item) => item.value === value)?.driverLabel ?? "")
              }
              showRouteHint={effectiveMode === "change"}
            />
          )}
        </SelectRow>
        <ResultDivider />
        <FieldLabel>Result</FieldLabel>
        <ResultRow>
          <ResultName>{currentDriverName}</ResultName>
          <span>→</span>
          <ResultTarget data-testid="swap-change-result-current-driver" isUnassigned={effectiveMode === "change"}>
            {effectiveMode === "swap" ? selectedSwapTarget?.routeLabel ?? "" : "Unassigned"}
          </ResultTarget>
        </ResultRow>
        <ResultRow>
          <ResultName data-testid="swap-change-result-target-name">{targetDriverLabel}</ResultName>
          <span>→</span>
          <ResultTarget data-testid="swap-change-result-target-route">{currentRouteLabel}</ResultTarget>
        </ResultRow>
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <CancelButton onClick={handleClose}>Cancel</CancelButton>
            <StyledButton intent="primary" onClick={handleSave} disabled={!selection}>
              Save
            </StyledButton>
          </>
        }
      />
    </StyledDialog>
  );
};

export default ChangeDriverDialog;
